// How to put a finished post on your own website.
//
// Session 42. Leena: "The problem someone like Cynthia is going to have is that
// she doesn't know how to put this into Squarespace ... we need to add
// something next to the download package that says, Instructions on how to
// upload to your system."
//
// She is right, and a text file inside a zip was not enough — the client has
// to download, unzip and notice it before it helps. This is the same content,
// readable in the portal before they download anything, opened from a button
// beside Download package.
//
// KEEP IN STEP with tools/lotus_package.py (_how_to_post / HOW_TO_SQUARESPACE),
// which writes HOW-TO-POST.txt into the zip. Same steps, same order, same
// wording where it matters. If you change one, change the other.
import { useState } from 'react';

const SQUARESPACE = {
  label: 'Squarespace',
  minutes: 10,
  steps: [
    {
      head: 'Look at it first',
      body: 'Unzip the download and double-click index.html. That is exactly how the '
          + 'post reads, with your photos where they go. Nothing is live yet, and nothing '
          + 'you do in this file changes anything.',
    },
    {
      head: 'Make a new blog post',
      body: 'In Squarespace: Pages, then your blog page, then the + button, then Blog Post.',
    },
    {
      head: 'Paste the words',
      body: 'Open squarespace-body.txt and copy all of it into the post body. The headings, '
          + 'bold text and links come across as they are — you should not need to fix '
          + 'any of the formatting.',
    },
    {
      head: 'Add the photos',
      body: 'They are in the squarespace-images folder, numbered in the order they appear. '
          + '01 is the one at the top of the post; the rest go under the heading named '
          + 'beside them in squarespace.txt. Drag each one into place in the editor.',
    },
    {
      head: 'Give each photo its description',
      body: 'Click a photo, then the gear icon, then Alt Text, and paste the "alt text" '
          + 'line for that photo from squarespace.txt. It is worth the two minutes: '
          + 'this is what Google reads to understand the picture, and what a screen '
          + 'reader says out loud to someone who cannot see it.',
    },
    {
      head: 'Copy the settings across',
      body: 'Open squarespace.txt. It lists, ready to copy: the post title; the SEO title '
          + 'and SEO description (the post, then Settings, then SEO); the URL slug '
          + '(Settings, then General, then Post URL); the excerpt, tags, category and '
          + 'author; and which image to set as the featured image.',
    },
    {
      head: 'Publish it',
      body: 'Set the publish date from squarespace.txt, or save it as a draft and schedule '
          + 'it for then. Then Publish.',
    },
    {
      head: 'Send us the link',
      body: 'Paste the live URL into this page, or just reply to the email we sent. We use '
          + 'it to check the post is being found, and to link future posts to it.',
    },
  ],
};

const WORDPRESS = {
  label: 'WordPress',
  minutes: 10,
  steps: [
    {
      head: 'Look at it first',
      body: 'Unzip the download and double-click index.html. That is exactly how the post '
          + 'reads. Nothing is live yet.',
    },
    {
      head: 'Make a new post',
      body: 'In WordPress: Posts, then Add New.',
    },
    {
      head: 'Paste the words',
      body: 'Open article.txt and copy it into the editor. If you use the block editor, '
          + 'paste into a single paragraph block and WordPress will split the headings '
          + 'and lists out for you.',
    },
    {
      head: 'Add the photos',
      body: 'They are in the images folder. hero is the one at the top — set that as the '
          + 'Featured Image. The body ones go under the headings listed in meta.json.',
    },
    {
      head: 'Give each photo its description',
      body: 'Select a photo and fill in Alternative Text with the alt line for it from '
          + 'meta.json. This is what Google reads and what a screen reader says out loud.',
    },
    {
      head: 'Copy the settings across',
      body: 'From meta.json: the title, the SEO description, the URL slug and the excerpt. '
          + 'If you use Yoast or Rank Math, the SEO title and description go in its panel '
          + 'at the bottom of the editor.',
    },
    {
      head: 'Publish it',
      body: 'Publish, or schedule it for the date in meta.json.',
    },
    {
      head: 'Send us the link',
      body: 'Paste the live URL into this page, or reply to our email.',
    },
  ],
};

const GENERIC = {
  label: 'your website',
  minutes: 10,
  steps: [
    {
      head: 'Look at it first',
      body: 'Unzip the download and double-click index.html. That is exactly how the post '
          + 'reads. Nothing is live yet.',
    },
    {
      head: 'Make a new post in your editor',
      body: 'Wherever you normally write a blog post on your site.',
    },
    {
      head: 'Paste the words',
      body: 'From article.txt into the post body.',
    },
    {
      head: 'Add the photos',
      body: 'From the images folder, in order. hero goes at the top; the body ones go under '
          + 'the headings listed in meta.json.',
    },
    {
      head: 'Give each photo its description',
      body: 'Paste the alt text for each photo from meta.json into your editor’s alt '
          + 'text field. It is what Google reads and what a screen reader says out loud.',
    },
    {
      head: 'Copy the settings across',
      body: 'The title, SEO description, URL slug and excerpt are all in meta.json.',
    },
    { head: 'Publish it', body: 'Or schedule it for the date in meta.json.' },
    { head: 'Send us the link', body: 'Through this page, or by replying to our email.' },
  ],
};

export function stepsFor(platform) {
  switch (String(platform || '').toLowerCase()) {
    case 'squarespace': return SQUARESPACE;
    case 'wordpress': return WORDPRESS;
    default: return GENERIC;
  }
}

/**
 * The button and the panel. Sits beside Download package, because the moment
 * somebody is about to download it is the moment they want to know what to do
 * with it.
 */
export default function PostingInstructions({ platform }) {
  const [open, setOpen] = useState(false);
  const guide = stepsFor(platform);
  return (
    <>
      <button type="button" className="btn" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide the instructions' : `How to put this on ${guide.label}`}
      </button>
      {open && (
        <section className="posting-steps">
          <h3>Putting this on {guide.label}</h3>
          <p className="hint">
            About {guide.minutes} minutes. Everything mentioned here is inside the
            download, and the same steps are in <code>HOW-TO-POST.txt</code> in
            there if you would rather read them alongside the files.
          </p>
          <ol>
            {guide.steps.map((s, i) => (
              <li key={i}>
                <strong>{s.head}.</strong> {s.body}
              </li>
            ))}
          </ol>
          <p className="hint">
            If anything looks wrong or will not go in, do not fight it — reply to
            our email and we will sort it out, or do it for you.
          </p>
        </section>
      )}
    </>
  );
}
